import React, { useEffect, useState, useMemo } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useFluidFormConfigClient } from './fluidFormConfig.client';
import { useFormSubmissionsClient } from '../submissions/formSubmissions.client';
import AddFormModal from '../components/AddFormModal';
import { formatDate } from '../date.format';
import { DeleteButton } from '../components/DeleteButton';
import PageLayout from '../components/PageLayout';
import FormTemplatesSlider from './FormTemplatesSlider';
import { Search, ArrowUpDown, FileText } from 'lucide-react';
import { trackEvent } from '../analytics';
function sanitizePromptText(raw) {
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    return (doc.body.textContent || '').slice(0, 4000).trim();
}
const SCOPED_KEY_PREFIX = 'ff_start_prompt:';
const FormsTable = () => {
    const client = useFluidFormConfigClient();
    const submissionsClient = useFormSubmissionsClient();
    const [forms, setForms] = useState([]);
    const [error, setError] = useState();
    const [submissionCounts, setSubmissionCounts] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [templatesOpen, setTemplatesOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Read ?p= prompt from URL (e.g. from /try-embed iframe redirect)
    const urlPrompt = useMemo(() => {
        const raw = searchParams.get('p');
        return raw ? sanitizePromptText(raw) : '';
    }, []);
    const [initialPrompt, setInitialPrompt] = useState(urlPrompt);
    // Auto-open the modal when arriving with a prompt
    useEffect(() => {
        if (urlPrompt) {
            setModalOpen(true);
            // Clean the ?p= param from the URL
            searchParams.delete('p');
            setSearchParams(searchParams, { replace: true });
        }
    }, []);
    const filteredForms = useMemo(() => {
        return forms
            .filter(form => {
            const query = searchQuery.toLowerCase();
            return form.formKey?.toLowerCase().includes(query) ||
                form.name?.toLowerCase().includes(query);
        })
            .sort((a, b) => {
            switch (sortBy) {
                case 'newest': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                case 'oldest': return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                case 'az': return (a.name || '').localeCompare(b.name || '');
                case 'za': return (b.name || '').localeCompare(a.name || '');
                case 'submissions':
                    return (submissionCounts[b.formKey] || 0) - (submissionCounts[a.formKey] || 0);
                default: return 0;
            }
        });
    }, [forms, searchQuery, sortBy, submissionCounts]);
    useEffect(() => {
        const fetchForms = async () => {
            try {
                setError(null);
                const formConfigs = await client.listFormConfigs(false);
                console.log('Fetched form configs:', formConfigs);
                setForms(formConfigs);
                // Fetch submission counts for all forms in a single API call
                const stats = await submissionsClient.getAllFormStats();
                setSubmissionCounts(stats);
            }
            catch (error) {
                console.error('Error fetching form configs:', error);
                setError('Failed to load forms. Please try again.');
            }
        };
        fetchForms();
    }, []);
    const handleAddForm = async (newForm, prompt) => {
        // @TODO: FLU-193 Handle this.
        await client.createFormConfig(newForm);
        if (prompt) {
            sessionStorage.setItem(`${SCOPED_KEY_PREFIX}${newForm.formKey}`, prompt);
        }
        // Refresh the forms list
        const formConfigs = await client.listFormConfigs(false);
        setForms(formConfigs);
        // Refresh submission counts
        const stats = await submissionsClient.getAllFormStats();
        setSubmissionCounts(stats);
        setInitialPrompt('');
        navigate(`/form-config/${newForm.formKey}`);
    };
    const handleDeleteForm = async (key) => {
        try {
            await client.deleteFormConfig(key);
            setForms(forms.filter(form => form.formKey !== key));
            // Also remove from submission counts
            const updatedCounts = { ...submissionCounts };
            delete updatedCounts[key];
            setSubmissionCounts(updatedCounts);
        }
        catch (error) {
            setError(`Failed to delete form ${key}. Please try again.`);
        }
    };
    return (React.createElement(PageLayout, { title: "Form Agents", subtitle: "Create and manage your AI-powered form agents. View analytics on the Dashboard.", icon: React.createElement(FileText, { className: "w-7 h-7 text-gray-700" }), error: error, headerActions: React.createElement("div", { className: "flex items-center gap-3" },
            React.createElement(Button, { basic: true, onClick: () => setTemplatesOpen(true) },
                React.createElement(Icon, { name: "grid layout" }),
                " Browse Templates"),
            React.createElement(Button, { primary: true, onClick: () => { trackEvent('create_form'); setModalOpen(true); } },
                React.createElement(Icon, { name: "plus" }),
                " New Form")), filterSection: React.createElement("div", { className: "flex flex-col md:flex-row items-start md:items-center justify-between gap-4" },
            React.createElement("div", { className: "relative w-full md:w-96" },
                React.createElement("div", { className: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" },
                    React.createElement(Search, { className: "h-4 w-4 text-gray-400" })),
                React.createElement("input", { type: "text", placeholder: "Search forms...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), className: "w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none hover:border-gray-300 transition-colors" })),
            React.createElement("div", { className: "relative" },
                React.createElement("div", { className: "absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none" },
                    React.createElement(ArrowUpDown, { className: "h-3.5 w-3.5 text-gray-500" })),
                React.createElement("select", { value: sortBy, onChange: (e) => setSortBy(e.target.value), className: "pl-8 pr-8 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none appearance-none cursor-pointer hover:border-gray-300 transition-colors" },
                    React.createElement("option", { value: "newest" }, "Newest First"),
                    React.createElement("option", { value: "oldest" }, "Oldest First"),
                    React.createElement("option", { value: "az" }, "Name A-Z"),
                    React.createElement("option", { value: "za" }, "Name Z-A"),
                    React.createElement("option", { value: "submissions" }, "Most Submissions")))) },
        React.createElement(FormTemplatesSlider, { onNewForm: () => setModalOpen(true), isOpen: templatesOpen, onOpenChange: setTemplatesOpen, hideButton: true }),
        React.createElement("div", { className: "bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" },
            React.createElement("div", { className: "grid grid-cols-12 gap-4 px-6 py-3 bg-gray-700 text-white text-base font-medium" },
                React.createElement("div", { className: "col-span-2" }, "KEY"),
                React.createElement("div", { className: "col-span-3" }, "NAME"),
                React.createElement("div", { className: "col-span-2" }, "SUBMISSIONS"),
                React.createElement("div", { className: "col-span-2" }, "CREATED AT"),
                React.createElement("div", { className: "col-span-2" }, "AUTHOR"),
                React.createElement("div", { className: "col-span-1 text-right" }, "ACTIONS")),
            React.createElement("div", { className: "divide-y divide-gray-200" },
                filteredForms.length === 0 && searchQuery && (React.createElement("div", { className: "px-6 py-12 text-center text-gray-500" },
                    React.createElement(Icon, { name: "search", size: "big", className: "mb-3 opacity-50" }),
                    React.createElement("p", { className: "text-lg font-medium" }, "No forms found"),
                    React.createElement("p", { className: "text-sm" },
                        "No forms match your search \"",
                        searchQuery,
                        "\""))),
                filteredForms.map((form) => (React.createElement("div", { key: form.formKey, className: "grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 transition-colors group" },
                    React.createElement("div", { className: "col-span-2" },
                        React.createElement(Link, { to: `/form-config/${form.formKey}`, className: "!text-orange-600 font-medium !text-base hover:underline" }, form.formKey)),
                    React.createElement("div", { className: "col-span-3" },
                        React.createElement("span", { className: "text-gray-900 text-base" }, form.name)),
                    React.createElement("div", { className: "col-span-2" },
                        React.createElement(Link, { to: `/submissions?form=${form.formKey}`, className: "!text-blue-600 !hover:text-blue-700 font-medium text-base hover:underline" }, submissionCounts[form.formKey] || 0)),
                    React.createElement("div", { className: "col-span-2" },
                        React.createElement("span", { className: "text-gray-600 text-base" }, formatDate(form.createdAt))),
                    React.createElement("div", { className: "col-span-2" }, form.authorData ? (React.createElement("div", null,
                        React.createElement("div", { className: "text-gray-900 text-base font-medium" }, `${form.authorData.firstName || ''} ${form.authorData.lastName || ''}`.trim() || 'Unnamed User'),
                        React.createElement("div", { className: "text-gray-500 text-xs" }, form.authorData.email))) : (React.createElement("span", { className: "text-gray-400 italic text-base" }, form.authorData?.userId))),
                    React.createElement("div", { className: "col-span-1 flex items-center justify-end space-x-2" },
                        React.createElement("button", { className: "p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors", title: "Edit", onClick: () => navigate(`/form-config/${form.formKey}`) },
                            React.createElement(Icon, { name: "edit" })),
                        React.createElement(DeleteButton, { iconOnly: true, onConfirm: () => handleDeleteForm(form.formKey), title: "Delete Form", message: "Are you sure you want to delete this form?" }))))))),
        React.createElement(AddFormModal, { open: modalOpen, onClose: () => { setModalOpen(false); setInitialPrompt(''); }, onSave: handleAddForm, initialPrompt: initialPrompt })));
};
export default FormsTable;
